import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, ChipSelector } from "@repo/ui";
import filterIcon from "../../../../assets/icons/filter.svg";
import AsideComponent from "../../../../components/AsideComponent/AsideComponent";
import DualRangeSlider from "../../../../components/DualRangeSlider/DualRangeSlider";
import { withTranslations } from "../../../../hoc/withTranslations/withTranslations";

interface FiltersComponentProps {
  onApplyFilters: (filters: Filters) => void;
  initialCategory?: string | null;
  categories: string[];
  brands: string[];
  priceRange: { min: number; max: number };
}

export interface Filters {
  category?: string;
  brand?: string;
  price?: { min: number; max: number };
  sort?: { field: string; order: "asc" | "desc" };
}

const LOCALE = "modules_Guests_components_TableContent_FiltersComponent";

const FiltersComponent: React.FC<FiltersComponentProps> = ({
  onApplyFilters,
  initialCategory = null,
  categories,
  brands,
  priceRange,
}) => {
  const { t } = useTranslation(LOCALE);
  const [isAsideVisible, setIsAsideVisible] = useState(false);
  const [category, setCategory] = useState<string | undefined>(
    initialCategory || ""
  );
  const [brand, setBrand] = useState<string | undefined>();
  const [price, setPrice] = useState<{ min: number; max: number }>({
    min: priceRange.min,
    max: priceRange.max,
  });
  const [sort, setSort] = useState<{ field: string; order: "asc" | "desc" }>();

  useEffect(() => {
    setPrice({ min: priceRange.min, max: priceRange.max });
  }, [priceRange.max, priceRange.min]);

  const handleApplyFilters = () => {
    onApplyFilters({
      category,
      brand,
      price,
      sort,
    });
    setIsAsideVisible(false);
  };

  const handleRangeChange = (values: { min: number; max: number }) => {
    setPrice(values);
  };

  const formatCurrency = (value: number) => `$${value.toLocaleString()}`;

  const handleSortChange = (id: string | null) => {
    if (id === "high-to-low") {
      setSort({ field: "price", order: "desc" });
    } else if (id === "low-to-high") {
      setSort({ field: "price", order: "asc" });
    } else {
      setSort(undefined);
    }
  };

  return (
    <>
      <div className="md:hidden">
        <button
          onClick={() => setIsAsideVisible(true)}
          className="p-2 border rounded flex items-center justify-center"
        >
          <img src={filterIcon} alt={t("filters.title")} className="h-6 w-6" />
        </button>
      </div>
      <div className="hidden md:block">
        <Button
          label={
            <div className="flex items-center justify-between">
              <span>{t("filters.title")}</span>
              <img
                src={filterIcon}
                alt={t("filters.title")}
                className="ml-2 h-4 w-4"
              />
            </div>
          }
          type="secondary"
          onClick={() => setIsAsideVisible(true)}
        />
      </div>
      <AsideComponent
        title={t("filters.title")}
        isVisible={isAsideVisible}
        onClose={() => setIsAsideVisible(false)}
      >
        <div
          className="space-y-8 max-h-[calc(100vh-150px)] overflow-y-auto px-4"
          style={{ paddingBottom: "4rem" }}
        >
          {/* Categories Selector */}
          <div>
            <label className="block mb-2">{t("filters.categories")}</label>
            <select
              className="w-full p-2 border"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              disabled={!!initialCategory}
            >
              <option value="">{t("filters.allCategories")}</option>
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          {/* Brands Selector */}
          <div>
            <label className="block mb-2">{t("filters.brands")}</label>
            <select
              className="w-full p-2 border"
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
            >
              <option value="">{t("filters.allBrands")}</option>
              {brands.map((brand) => (
                <option key={brand} value={brand}>
                  {brand}
                </option>
              ))}
            </select>
          </div>

          {/* Price Slider */}
          <div>
            <label className="block mb-2">{t("filters.price")}</label>
            <DualRangeSlider
              min={priceRange.min}
              max={priceRange.max}
              step={1}
              onChange={handleRangeChange}
              formatValue={formatCurrency}
            />
          </div>

          {/* Sort Selector */}
          <div>
            <label className="block mb-2">{t("filters.sortBy")}</label>
            <ChipSelector
              items={[
                { id: "relevant", label: t("filters.relevant") },
                { id: "high-to-low", label: t("filters.highPrice") },
                { id: "low-to-high", label: t("filters.lowPrice") },
              ]}
              selected={
                !sort
                  ? "relevant"
                  : sort.order === "desc"
                    ? "high-to-low"
                    : "low-to-high"
              }
              onChange={handleSortChange}
            />
          </div>
        </div>

        {/* Apply Filters Button */}
        <div
          className="absolute bottom-4 left-0 w-full flex justify-center px-4"
          style={{ zIndex: 10 }}
        >
          <Button
            label={t("filters.applyFilters")}
            onClick={handleApplyFilters}
          />
        </div>
      </AsideComponent>
    </>
  );
};

export default withTranslations(LOCALE)(FiltersComponent);
